import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { projectsDataStore } from "Stores";

interface ViewpointToSubmit extends API_BcfServerViewpoint {
  toSubmit?: boolean;
}

export interface ViewpointWithImage extends ViewpointToSubmit {
  imageSrc: string;
}

export class ViewpointManager {
  topicId: string;
  projectGuid: string;
  viewpoints: ViewpointWithImage[] = [];
  private createQueue: ViewpointToSubmit[] = [];
  private removeQueue: string[] = [];

  constructor() {
    this.projectGuid = projectsDataStore.bcfGuid;
    makeObservable<ViewpointManager, "postItem" | "deleteItem" | "createQueue" | "removeQueue">(this, {
      viewpoints: observable,
      createQueue: observable,
      removeQueue: observable,
      hasPendingChanges: computed,
      init: action.bound,
      fetch: action.bound,
      create: action.bound,
      remove: action.bound,
      postItem: action.bound,
      deleteItem: action.bound,
      save: action.bound,
      saveSingleViewpoint: action.bound,
      cancelPendingViewpoints: action.bound,
    });
  }

  get hasPendingChanges(): boolean {
    return this.createQueue.length > 0 || this.removeQueue.length > 0;
  }

  async init(topicId: string) {
    if (topicId !== this.topicId) {
      this.topicId = topicId;
      this.clear();
      this.fetch();
    }
  }

  clear() {
    this.viewpoints.forEach((item) => {
      if (!item.toSubmit) {
        URL.revokeObjectURL(item.imageSrc);
      }
    });
    this.createQueue = [];
    this.removeQueue = [];
    this.viewpoints = [];
  }

  cancelPendingViewpoints() {
    this.viewpoints.forEach((item) => {
      if (item.toSubmit) {
        URL.revokeObjectURL(item.imageSrc);
      }
    });
    this.viewpoints = this.viewpoints.filter((item) => !item.toSubmit);
    this.createQueue = [];
    this.removeQueue = [];
    this.topicId = undefined;
  }

  async fetch() {
    try {
      const response = await API.getData<API_BcfServerViewpoint[]>(
        API_URL.BCF_SERVER_VIEWPOINTS.bcfServerGetViewpoints(this.projectGuid, this.topicId),
        { includeAuthorization: true }
      );
      const viewpointSnapshots = await Promise.all(response.map((item) => this.getImageSrcForViewpoint(item)));
      runInAction(() => {
        this.viewpoints = response.map((item, idx) => ({ ...item, imageSrc: viewpointSnapshots[idx] }));
      });
    } catch (error) {
      throw error;
    }
  }

  create(viewpoint: API_BcfServerViewpoint) {
    this.createQueue.push({ ...viewpoint, toSubmit: true });
    this.viewpoints.push({ ...viewpoint, imageSrc: viewpoint.snapshot.snapshot_data, toSubmit: true });
  }

  remove(viewpointId: API_BcfServerViewpoint["guid"]) {
    const viewpoint = this.viewpoints.find((item) => item.guid === viewpointId);
    if (viewpoint.toSubmit) {
      this.createQueue = this.createQueue.filter((item) => item.guid !== viewpoint.guid);
    } else {
      this.removeQueue.push(viewpointId);
    }
    this.viewpoints = this.viewpoints.filter((item) => item.guid !== viewpoint.guid);
  }

  async save() {
    try {
      let didSomething = false;
      if (this.removeQueue.length) {
        await Promise.all(this.removeQueue.map(this.deleteItem));
      }
      if (this.createQueue.length) {
        await Promise.all(this.createQueue.map(this.postItem));
        didSomething = true;
      }
      runInAction(() => {
        this.createQueue = [];
        this.removeQueue = [];
      });
      if (didSomething) {
        this.fetch();
      }
    } catch (error) {
      throw error;
    }
  }

  async saveSingleViewpoint(viewpointGuid: string): Promise<string | null> {
    try {
      // Find the viewpoint in createQueue
      const viewpointToSubmit = this.createQueue.find((item) => item.guid === viewpointGuid);

      if (!viewpointToSubmit) {
        // Viewpoint not found in createQueue, might already be submitted
        return null;
      }

      // Preserve unsubmitted viewpoints before fetch (they will be lost otherwise)
      const remainingUnsubmittedViewpoints = this.createQueue
        .filter((item) => item.guid !== viewpointGuid)
        .map((item) => {
          // Find the corresponding viewpoint in viewpoints array to preserve imageSrc
          const existingViewpoint = this.viewpoints.find((vp) => vp.guid === item.guid);
          return {
            ...item,
            imageSrc: existingViewpoint?.imageSrc || item.snapshot?.snapshot_data || "",
          };
        });

      // Submit only this viewpoint and get the server response with new GUID
      const submittedViewpoint = await this.postItem(viewpointToSubmit);

      // Remove from createQueue
      runInAction(() => {
        this.createQueue = this.createQueue.filter((item) => item.guid !== viewpointGuid);
      });

      // Refresh viewpoints to get the updated list with the newly submitted viewpoint
      await this.fetch();

      // Restore unsubmitted viewpoints back to the viewpoints array
      runInAction(() => {
        this.viewpoints = [...this.viewpoints, ...remainingUnsubmittedViewpoints];
      });

      // Return the new GUID from the server response
      return submittedViewpoint.guid;
    } catch (error) {
      throw error;
    }
  }

  private async postItem(viewpoint: ViewpointToSubmit): Promise<API_BcfServerViewpoint> {
    try {
      // extract guid, toSubmit properties from request body
      const { guid: _1, toSubmit: _2, ...formData } = viewpoint;
      const response = await API.newData<API_BcfServerViewpoint>(
        API_URL.BCF_SERVER_VIEWPOINTS.bcfServerPostViewpoint(this.projectGuid, this.topicId),
        formData
      );
      return response;
      // API.newData<API_BcfServerViewpoint>(
      //   API_URL.BCF_SERVER_COMMENTS.bcfServerPostComments(this.projectGuid, this.topicId),
      //   { viewpoint_guid: response.guid }
      // );
    } catch (error) {
      throw error;
    }
  }

  private async deleteItem(viewpointId: string) {
    return API.deleteData(
      API_URL.BCF_SERVER_VIEWPOINTS.bcfServerDeleteViewpoint(this.projectGuid, this.topicId, viewpointId)
    );
  }

  async getImageSrcForViewpoint(viewpoint: API_BcfServerViewpoint) {
    if (viewpoint.snapshot) {
      const file = await API.getData(
        API_URL.BCF_SERVER_VIEWPOINTS.bcfServerGetViewpointSnapshot(this.projectGuid, this.topicId, viewpoint.guid),
        null,
        { responseType: "blob" }
      );
      return URL.createObjectURL(file);
    } else {
      return "error";
    }
  }
}
